// src/frontend/js/modern/lib/toastr-lite.js
//
// Modern Vanilla JS reimplementation of Toastr (v2.1.4)
// Performance Optimizations:
//   - Zero-JS animations: All transitions handled by CSS to prevent main-thread blocking
//   - CSS-driven progress bars: Eliminates setInterval/rAF loops entirely
//   - Decoupled lifecycle: Each toast manages its own timers independently
//   - Memory safety: Automatic cleanup of listeners and DOM references
//   - Full compatibility with legacy _toast.scss without modifications
//
// Configuration:
//   - Runtime config applied via applyConfig() from window.wlfmc_l10n
class ToastrLite {
	constructor() {
		// Default options matching original Toastr v2.1.4 for SCSS compatibility
		this.options = {
			tapToDismiss: true,
			toastClass: 'toast',
			containerId: 'toast-container',
			debug: false,
			closeButton: false,
			showDuration: 300,
			hideDuration: 1000,
			closeOnHover: true,
			extendedTimeOut: 1000,
			iconClasses: {
				error: 'toast-error',
				info: 'toast-info',
				success: 'toast-success',
				warning: 'toast-warning'
			},
			iconClass: 'toast-info',
			positionClass: 'toast-top-left',
			timeOut: 5000,
			titleClass: 'toast-title',
			messageClass: 'toast-message',
			escapeHtml: false,
			target: 'body',
			closeHtml: '<button type="button">&times;</button>',
			closeClass: 'toast-close-button',
			newestOnTop: true,
			preventDuplicates: false,
			progressBar: true,
			progressClass: 'toast-progress',
			rtl: false,
			maxVisible: 3 // 0 = unlimited, matches original behavior
		};
		this._toastId = 0;
		this._activeMessages = new Set();
		this._container = null;
		this._activeToasts = new Map();
		this._previousToastMessage = undefined;
		this._configured = false;
		// Bind event handlers once to avoid creating closures per toast
		this._handleMouseEnter = this._handleMouseEnter.bind(this);
		this._handleMouseLeave = this._handleMouseLeave.bind(this);
		this._handleTap = this._handleTap.bind(this);
		this._handleCloseClick = this._handleCloseClick.bind(this);
	}

	/* ── Configuration ─────────────────────────────────────────────── */
	/**
	 * Apply runtime configuration from localized data (wlfmc_l10n).
	 * Idempotent: safe to call multiple times.
	 */
	applyConfig() {
		if (this._configured) return;
		const l10n = window.wlfmc_l10n || {};
		this.options.rtl = !!l10n.is_rtl;
		this.options.positionClass = this._getPositionClass();
		this._configured = true;
	}

	_getPositionClass() {
		const l10n = window.wlfmc_l10n || {};
		const pos = l10n.toast_position;
		if (pos && pos !== 'default') return pos;
		return l10n.is_rtl ? 'toast-top-right' : 'toast-top-left';
	}

	/* ── Container Management ──────────────────────────────────────── */
	_getContainer(create = true) {
		if (this._container && document.body.contains(this._container)) {
			return this._container;
		}
		this._container = document.getElementById(this.options.containerId);
		if (!this._container && create) {
			this._container = document.createElement('div');
			this._container.id = this.options.containerId;
			this._container.className = this.options.positionClass;

			this._container.style.overflowAnchor = 'none';
			const target = document.querySelector(this.options.target) || document.body;
			target.appendChild(this._container);
		}
		return this._container;
	}

	_cleanupContainer() {
		if (this._activeToasts.size === 0 && this._container) {
			requestAnimationFrame(() => {
				if (this._activeToasts.size === 0 && this._container?.parentNode) {
					this._container.parentNode.removeChild(this._container);
					this._container = null;
					this._activeMessages.clear();
					this._previousToastMessage = undefined;
				}
			});
		}
	}

	/* ── HTML Helpers ──────────────────────────────────────────────── */
	_escapeHtml(source) {
		if (source == null) return '';
		return String(source)
			.replace(/&/g, '&amp;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;');
	}

	/* ── Toast Creation & Lifecycle ────────────────────────────────── */
	_notify(type, message, title, optionsOverride) {
		const options = { ...this.options, ...(optionsOverride || {}) };
		const iconClass = options.iconClasses[type] || options.iconClass;

		// Duplicate prevention: check against both active set and previous toast
		if (options.preventDuplicates) {
			if (message === this._previousToastMessage) return null;
			if (message && this._activeMessages.has(String(message))) return null;
		}

		// Evict oldest toasts if maxVisible is configured
		this._evictIfNeeded(options);

		const container = this._getContainer(true);

		const toastEl = this._createToastElement(type, message, title, iconClass, options);

		// Initialize per-toast metadata
		const meta = {
			message: message ? String(message) : '',
			timeoutId: null,
			extTimeoutId: null,
			isPaused: false,
			remainingTime: options.timeOut,
			startTime: Date.now(),
			options: options
		};

		this._activeToasts.set(toastEl, meta);

		if (message) {
			this._activeMessages.add(String(message));
			this._previousToastMessage = message;
		}

		const scrollX = window.scrollX;
		const scrollY = window.scrollY;

		// Insert based on sort order
		if (options.newestOnTop) {
			container.insertBefore(toastEl, container.firstChild);
		} else {
			container.appendChild(toastEl);
		}

		// Restore scroll position immediately after DOM mutation
		// to neutralize any browser-initiated scroll adjustment.
		window.scrollTo(scrollX, scrollY);

		// Trigger entry animation via CSS class (no JS animation)
		requestAnimationFrame(() => {
			toastEl.classList.add('toast-visible');
			if (options.progressBar) {
				this._startProgress(toastEl, options.timeOut);
			}
			this._scheduleHide(toastEl, options.timeOut);
		});

		this._toastId++;
		return toastEl;
	}

	_createToastElement(type, message, title, iconClass, options) {
		const toastEl = document.createElement('div');
		toastEl.className = `${options.toastClass} ${iconClass}`;
		if (options.rtl) toastEl.classList.add('rtl');
		toastEl.setAttribute('aria-live', type === 'error' || type === 'warning' ? 'assertive' : 'polite');
		toastEl.dataset.toastId = ++this._toastId;

		// Close button
		if (options.closeButton) {
			const wrapper = document.createElement('div');
			wrapper.innerHTML = options.closeHtml;
			const closeBtn = wrapper.firstElementChild;
			if (closeBtn) {
				closeBtn.classList.add(options.closeClass);
				closeBtn.setAttribute('role', 'button');
				toastEl._closeBtn = closeBtn;
				toastEl.insertBefore(closeBtn, toastEl.firstChild);
			}
		}

		// Title
		if (title) {
			const titleEl = document.createElement('div');
			titleEl.className = options.titleClass;
			titleEl.textContent = options.escapeHtml ? this._escapeHtml(title) : title;
			toastEl.appendChild(titleEl);
		}

		// Message
		if (message) {
			const msgEl = document.createElement('div');
			msgEl.className = options.messageClass;
			if (options.escapeHtml) {
				msgEl.textContent = this._escapeHtml(message);
			} else {
				msgEl.innerHTML = message;
			}
			toastEl.appendChild(msgEl);
		}

		// Progress bar (CSS-driven, no JS updates needed)
		if (options.progressBar) {
			const progressEl = document.createElement('div');
			progressEl.className = options.progressClass;
			toastEl.appendChild(progressEl);
			toastEl._progressEl = progressEl;
		}

		// Attach delegated-friendly event listeners
		if (options.tapToDismiss) {
			toastEl.addEventListener('click', this._handleTap);
		}
		if (options.closeButton && toastEl._closeBtn) {
			toastEl._closeBtn.addEventListener('click', this._handleCloseClick);
		}
		if (options.closeOnHover) {
			toastEl.addEventListener('mouseenter', this._handleMouseEnter);
			toastEl.addEventListener('mouseleave', this._handleMouseLeave);
		}

		return toastEl;
	}

	/* ── CSS-Driven Progress Bar ───────────────────────────────────── */
	/**
	 * Starts progress bar using pure CSS transition.
	 * Eliminates the need for any JS animation loop or interval.
	 */
	_startProgress(toastEl, durationMs) {
		const el = toastEl._progressEl;
		if (!el) return;
		// Reset to full width without transition
		el.style.transition = 'none';
		el.style.width = '100%';
		// Force reflow to ensure browser registers the 100% state
		void el.offsetWidth;
		// Animate to 0% over the exact timeout duration
		el.style.transition = `width ${durationMs}ms linear`;
		el.style.width = '0%';
	}

	_pauseProgress(toastEl) {
		const el = toastEl._progressEl;
		if (!el) return;
		// Freeze at current computed width
		const currentWidth = getComputedStyle(el).width;
		el.style.transition = 'none';
		el.style.width = currentWidth;
	}

	_resumeProgress(toastEl, remainingMs) {
		const el = toastEl._progressEl;
		if (!el || remainingMs <= 0) return;
		void el.offsetWidth; // Trigger reflow
		el.style.transition = `width ${remainingMs}ms linear`;
		el.style.width = '0%';
	}

	/* ── Timer Management ──────────────────────────────────────────── */
	_scheduleHide(toastEl, delay) {
		const meta = this._activeToasts.get(toastEl);
		if (!meta) return;
		clearTimeout(meta.timeoutId);
		clearTimeout(meta.extTimeoutId);
		meta.startTime = Date.now();
		meta.remainingTime = delay;
		meta.timeoutId = setTimeout(() => {
			this._hideToast(toastEl);
		}, delay);
	}

	_handleMouseEnter(e) {
		const toastEl = e.currentTarget;
		const meta = this._activeToasts.get(toastEl);
		if (!meta || meta.isPaused) return;
		meta.isPaused = true;
		clearTimeout(meta.timeoutId);
		clearTimeout(meta.extTimeoutId);
		// Accurately calculate remaining time
		const elapsed = Date.now() - meta.startTime;
		meta.remainingTime = Math.max(0, meta.remainingTime - elapsed);
		this._pauseProgress(toastEl);
	}

	_handleMouseLeave(e) {
		const toastEl = e.currentTarget;
		const meta = this._activeToasts.get(toastEl);
		if (!meta || !meta.isPaused) return;
		meta.isPaused = false;
		const nextDelay = meta.options.extendedTimeOut > 0
			? meta.options.extendedTimeOut
			: meta.remainingTime;
		meta.remainingTime = nextDelay;
		this._resumeProgress(toastEl, nextDelay);
		this._scheduleHide(toastEl, nextDelay);
	}

	_handleTap(e) {
		if (e.target.closest('.toast-close-button')) return;
		const toastEl = e.currentTarget;
		const meta = this._activeToasts.get(toastEl);
		if (meta?.options.tapToDismiss) {
			this._hideToast(toastEl);
		}
	}

	_handleCloseClick(e) {
		e.stopPropagation();
		const toastEl = e.currentTarget.closest('.toast');
		if (toastEl) this._hideToast(toastEl, true);
	}

	/* ── Eviction & Removal ────────────────────────────────────────── */
	_evictIfNeeded(options) {
		const max = options.maxVisible;
		if (!max || max <= 0 || this._activeToasts.size < max) return;
		const excess = this._activeToasts.size - max + 1;
		let count = 0;
		for (const [el] of this._activeToasts) {
			if (count >= excess) break;
			this._removeImmediately(el);
			count++;
		}
	}

	_hideToast(toastEl, force = false) {
		const meta = this._activeToasts.get(toastEl);
		if (!meta) return;
		// Respect focus guard unless forced (matches original behavior)
		if (!force && toastEl.contains(document.activeElement)) return;
		clearTimeout(meta.timeoutId);
		clearTimeout(meta.extTimeoutId);
		this._activeToasts.delete(toastEl);
		if (meta.message) this._activeMessages.delete(meta.message);
		// Exit animation via CSS class swap
		toastEl.classList.remove('toast-visible');
		toastEl.classList.add('toast-hiding');
		toastEl.style.pointerEvents = 'none';
		setTimeout(() => {
			if (toastEl.parentNode) {
				toastEl.parentNode.removeChild(toastEl);
			}
			this._detachListeners(toastEl);
			this._cleanupContainer();
		}, meta.options.hideDuration);
	}

	_removeImmediately(toastEl) {
		const meta = this._activeToasts.get(toastEl);
		if (meta) {
			clearTimeout(meta.timeoutId);
			clearTimeout(meta.extTimeoutId);
			if (meta.message) this._activeMessages.delete(meta.message);
			this._activeToasts.delete(toastEl);
		}
		if (toastEl.parentNode) {
			toastEl.parentNode.removeChild(toastEl);
		}
		this._detachListeners(toastEl);
		this._cleanupContainer();
	}

	_detachListeners(toastEl) {
		toastEl.removeEventListener('click', this._handleTap);
		toastEl.removeEventListener('mouseenter', this._handleMouseEnter);
		toastEl.removeEventListener('mouseleave', this._handleMouseLeave);
		if (toastEl._closeBtn) {
			toastEl._closeBtn.removeEventListener('click', this._handleCloseClick);
		}
	}

	/* ── Public API ────────────────────────────────────────────────── */
	error(message, title, optionsOverride) {
		return this._notify('error', message, title, optionsOverride);
	}

	info(message, title, optionsOverride) {
		return this._notify('info', message, title, optionsOverride);
	}

	success(message, title, optionsOverride) {
		return this._notify('success', message, title, optionsOverride);
	}

	warning(message, title, optionsOverride) {
		return this._notify('warning', message, title, optionsOverride);
	}

	clear(toastEl, clearOptions) {
		const container = this._getContainer(false);
		if (!container) return;
		if (toastEl) {
			this._hideToast(toastEl, clearOptions?.force);
		} else {
			// Snapshot to avoid mutation during iteration
			[...this._activeToasts.keys()].forEach(el => {
				this._hideToast(el, clearOptions?.force);
			});
		}
	}

	remove(toastEl) {
		if (toastEl) this._removeImmediately(toastEl);
	}
}

const toastrLite = new ToastrLite();

// Auto-apply config if localization data is already available
if (typeof window.wlfmc_l10n !== 'undefined') {
	toastrLite.applyConfig();
}

export default toastrLite;
export { ToastrLite };
